/** A request that failed, as much as the app may say about it (AD1: admin says all of it). */
export type RequestFailure = {
  /** HTTP status; null when the server never answered (network, CORS, timeout). */
  status: number | null;
  /** Why there is no status, as RTK Query says it (`FETCH_ERROR`, `TIMEOUT_ERROR`…). */
  statusText?: string;
  method: string;
  url: string;
  /** The backend's own message, when it sent one. */
  message?: string;
  /** What the user was doing, in the UI's words ("Saving the custom field"). */
  action?: string;
};

/**
 * How much a notification says (DECISIONS D3). Layout's is concise (it hides
 * endpoints and statuses); admin's is verbose (AD1: status, endpoint, time).
 */
export type NotificationVerbosity = "concise" | "verbose";

/**
 * What a failed request's toast says, by the app's verbosity (GEN5-5269
 * ARCHITECTURE item 14, DECISIONS D3). One pure function for both apps:
 * layout (concise) shows the backend's message and nothing of the request;
 * admin (verbose, AD1) adds the method, URL, status and UTC time under it.
 */
export type FailureText = { message?: string; debug?: string };

export const UNREACHABLE_MESSAGE =
  "The server could not be reached. Check your connection.";

/** AD1's debug line: method, URL, status and the UTC time. */
export const describeFailure = (
  failure: RequestFailure,
  now: Date = new Date()
): string => {
  const utcTimestamp = now.toISOString().replace(/\.\d{3}Z$/, "Z");
  const status = failure.status ?? failure.statusText ?? "FETCH_ERROR";
  return `${failure.method} ${failure.url} · ${status} · ${utcTimestamp} UTC`;
};

export const failureText = (
  failure: RequestFailure,
  verbosity: NotificationVerbosity,
  now: Date = new Date()
): FailureText =>
  verbosity === "concise"
    ? { message: failure.message }
    : {
        message:
          failure.status === null ? UNREACHABLE_MESSAGE : failure.message,
        debug: describeFailure(failure, now),
      };

const messageOf = (value: unknown): string | undefined =>
  typeof value === "object" &&
  value !== null &&
  "message" in value &&
  typeof value.message === "string"
    ? value.message
    : undefined;

/**
 * What a rejected `.unwrap()` says, for a toast: RTK Query throws either a
 * FetchBaseQueryError (`{ status, data: { message } }`) or a SerializedError
 * (`{ message }`).
 */
export const unwrapFailureText = (error: unknown): FailureText => ({
  message:
    (typeof error === "object" && error !== null && "data" in error
      ? messageOf(error.data)
      : undefined) ?? messageOf(error),
});
